import type {
  AccountInfo,
  IPublicClientApplication,
} from '@azure/msal-browser';
import {
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import config from 'walden-fast/config/environment';

export default class SessionService extends Service {
  @service('router') declare routerService: RouterService;

  _msalInstance: IPublicClientApplication | null = null;

  _testState: AccountInfo | null = null;

  @tracked accessToken?: string;
  @tracked tokenExpires?: Date | null;

  // Guard for multiple concurrent token refreshes
  private tokenRefreshPromise?: Promise<void>;

  async msalInstance() {
    if (this._msalInstance) {
      return this._msalInstance;
    }

    this._msalInstance =
      await PublicClientApplication.createPublicClientApplication(
        this.msalConfig,
      );
    return this._msalInstance;
  }

  get msalConfig() {
    return {
      auth: {
        authority: config.msal.authority,
        clientId: config.msal.clientId,
        navigateToLoginRequestUrl: false,
        postLogoutRedirectUri: config.msal.postLogoutRedirectUri,
        redirectUri: `${window.location.origin}${config.msal.redirectUri}`,
      },
    };
  }

  get authRequest() {
    return {
      scopes: [`${config.msal.scope}`],
    };
  }

  get attemptedUrlKey() {
    return (
      config.modulePrefix +
      (config.environment === 'test' ? '-test' : '') +
      'attempted-url'
    );
  }

  get accessTokenValid() {
    return (
      !!this.accessToken &&
      !!this.tokenExpires &&
      this.tokenExpires > new Date()
    );
  }

  async isAuthenticated() {
    return Boolean((await this.state())?.username);
  }

  async state(): Promise<AccountInfo | null> {
    const instance = await this.msalInstance();

    return instance.getActiveAccount();
  }

  async redirectToLoginIfNotAuthenticated() {
    const instance = await this.msalInstance();

    if ((await this.isAuthenticated()) === false) {
      window.localStorage.setItem(
        this.attemptedUrlKey,
        window.location.pathname + window.location.search,
      );
      await instance.loginRedirect(this.authRequest);
    }
  }

  async validateCallbackAndRedirectToAttemptedUrl(): Promise<Transition> {
    const instance = await this.msalInstance();
    const response = await instance.handleRedirectPromise();
    const state = await this.state();

    if (response === null && state === null) {
      // Redirect to login if authentication failed
      void this.redirectToLoginIfNotAuthenticated();
      return;
    }

    if (response) {
      instance.setActiveAccount(response?.account);
    }

    const attemptedUrl =
      window.localStorage.getItem(this.attemptedUrlKey) || config.rootURL;
    const routeInfo = this.routerService.recognize(attemptedUrl);

    const redirectRoute = routeInfo?.name.startsWith('app.')
      ? attemptedUrl
      : 'app';
    window.localStorage.removeItem(this.attemptedUrlKey);
    return this.routerService.replaceWith(redirectRoute);
  }

  async fetchAccessToken() {
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    this.tokenRefreshPromise = (async () => {
      try {
        const state = await this.state();
        if (state && Object.keys(state).length) {
          const instance = await this.msalInstance();
          try {
            const response = await instance.acquireTokenSilent(
              this.authRequest,
            );
            this.accessToken = response?.accessToken;
            this.tokenExpires = response.expiresOn;
          } catch (error: unknown) {
            if (error instanceof InteractionRequiredAuthError) {
              // acquireTokenRedirect will automatically check if there is an interaction in progress
              // and throw BrowserAuthError if there is. So we don't need to check manually.
              return instance.acquireTokenRedirect(this.authRequest);
            } else {
              console.error(error);
              throw error;
            }
          }
        }
      } finally {
        this.tokenRefreshPromise = undefined;
      }
    })();

    return this.tokenRefreshPromise;
  }

  clearTokens() {
    this.accessToken = undefined;
  }

  async unauthenticate() {
    await (
      await this.msalInstance()
    ).logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}${config.msal.redirectUri}`,
    });
  }
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:session')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('session') declare altName: SessionService;`.
declare module '@ember/service' {
  interface Registry {
    session: SessionService;
  }
}
